/**
 * TanStack Query Hook for Tax Revenue Trends KPI
 *
 * This hook provides access to tax revenue trends KPI data from the database,
 * including total tax revenue in millions EUR and year-over-year percentage change.
 *
 * Database Source: web.v_kpi_tax_revenue_trends_v1 view
 *
 * @module useKpiTaxRevenueTrendsV1
 * @category Database Hooks
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

/**
 * Interface for Tax Revenue Trends KPI data
 */
interface KpiTaxRevenueTrends {
  tax_revenue_millions_eur: number;
  tax_revenue_change_percentage: number;
}

/**
 * Fetches tax revenue trends KPI data from the database
 *
 * @returns {Promise<KpiTaxRevenueTrends>} KPI data object with revenue amount and change percentage
 * @throws {Error} If the database query fails or returns invalid data
 * @internal
 */
const fetchKpiTaxRevenueTrends = async (): Promise<KpiTaxRevenueTrends> => {
  const { data, error } = await supabase
    .schema('web')
    .from('v_kpi_tax_revenue_trends_v1')
    .select('*')
    .single();

  if (error) {
    throw new Error(`Failed to fetch tax revenue trends KPI: ${error.message}`);
  }

  if (!data) {
    throw new Error('No data returned from tax revenue trends KPI query');
  }

  return data;
};

/**
 * React Hook for Tax Revenue Trends KPI Data
 *
 * Fetches and caches the tax revenue trends KPI data using TanStack Query.
 * Includes total tax revenue in millions EUR and YoY change percentage.
 *
 * Query Configuration:
 * - Query Key: ['kpi', 'tax-revenue-trends', 'v1']
 * - Stale Time: 1 hour (3600000ms)
 * - Refetch on Window Focus: false
 * - Refetch on Mount: false
 * - Retry Attempts: 2
 *
 * @returns {UseQueryResult<KpiTaxRevenueTrends>} TanStack Query result object
 *
 * @example
 * ```typescript
 * const { data, isLoading, isError } = useKpiTaxRevenueTrendsV1();
 *
 * if (isLoading) return <Spinner />;
 * if (isError) return <ErrorMessage />;
 *
 * return (
 *   <div>
 *     <p>Tax Revenue: €{data.tax_revenue_millions_eur}M</p>
 *     <p>Change: {data.tax_revenue_change_percentage}%</p>
 *   </div>
 * );
 * ```
 */
export const useKpiTaxRevenueTrendsV1 = (): UseQueryResult<KpiTaxRevenueTrends> => {
  return useQuery<KpiTaxRevenueTrends>({
    queryKey: ['kpi', 'tax-revenue-trends', 'v1'],
    queryFn: fetchKpiTaxRevenueTrends,
    staleTime: 3600000, // 1 hour in milliseconds
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  });
};
